import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import { useLocation } from 'react-router-dom'
import {
  BoxV4,
  DatePickerV2,
  monthYearWithComaFormat,
  NoteColors,
  NoteV2,
  SelectV4,
  ButtonV2,
  LabraToolTipV2,
} from '@labrav/react-components'
import { useStyles } from './ContractDuration.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendarDay,
  faPen,
  faListCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { get, isEmpty } from 'lodash'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { Grid, SelectChangeEvent } from '@mui/material'
import { DateTime } from 'luxon'
import {
  MicrosoftFormFAQs,
  MicrosoftFormFAQsItem,
} from '../MicrosoftFormFAQs/MicrosoftFormFAQs'
import {
  ACCEPTED_DATE_LABEL,
  applyPurchaseTermsMetaData,
  getDefaultPurchaseExpiryDate,
  parseAcceptedDateMode,
  PurchaseTermsMetaDataItem,
  shouldOpenPurchaseTermsEditView,
  toPurchaseStartMonthPickerDate,
} from '../../../../modules/privateOffer/privateOfferCreation/utils/microsoftPurchaseTermsUtil'
import { useTimeZone } from '../../../../common/utils/hooks/useTimeZone'
import {
  FieldMetadata,
  FieldMetadataField,
} from '../../../../modules/privateOffer/types'
import { Product } from '../../../../modules/productsListing/types'

const startDateOptions = [
  { label: ACCEPTED_DATE_LABEL, value: true },
  { label: 'Specified month', value: false },
]

export {
  ACCEPTED_DATE_DEFAULT_DISPLAY_LABEL,
  ACCEPTED_DATE_LABEL,
  getDefaultPurchaseExpiryDate,
} from '../../../../modules/privateOffer/privateOfferCreation/utils/microsoftPurchaseTermsUtil'

export const ContractDuration = ({
  fieldMetadata,
}: {
  product: Product
  fieldMetadata?: FieldMetadata
}) => {
  const formikProps = useFormikContext() as FormikProps<Record<string, unknown>>
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formikProps
  const { classes, cx } = useStyles()
  const { timeZone } = useTimeZone()
  const { search } = useLocation()
  const isCloneFlow = new URLSearchParams(search).get('from') === 'clone'
  const [shouldBlinkCloneDates, setShouldBlinkCloneDates] =
    useState(isCloneFlow)

  const isPurchaseTermsReadOnly = (
    fieldMetadata?.purchaseTerms as FieldMetadataField
  )?.readOnly
  const isOfferStartDateHidden = (
    fieldMetadata?.offerStartDate as FieldMetadataField
  )?.hidden
  const isOfferStartDateReadOnly = (
    fieldMetadata?.offerStartDate as FieldMetadataField
  )?.readOnly
  const isOfferExpirationDateHidden = (
    fieldMetadata?.offerExpirationDate as FieldMetadataField
  )?.hidden
  const isOfferExpirationDateReadOnly = (
    fieldMetadata?.offerExpirationDate as FieldMetadataField
  )?.readOnly

  useEffect(() => {
    if (!isCloneFlow) {
      return
    }

    const timer = window.setTimeout(() => {
      setShouldBlinkCloneDates(false)
    }, 2400)

    return () => window.clearTimeout(timer)
  }, [isCloneFlow])

  const acceptedDateSelected = parseAcceptedDateMode(values.variableStartDate)

  const purchaseTermsViewState = {
    purchaseTermsCustomized: values.purchaseTermsCustomized as
      | boolean
      | undefined,
    variableStartDate: values.variableStartDate,
    offerStartDate: values.offerStartDate as string | null | undefined,
    offerExpirationDate: values.offerExpirationDate as
      | string
      | null
      | undefined,
    createdAt: values.createdAt as string | null | undefined,
    metaData: values.metaData as PurchaseTermsMetaDataItem[] | undefined,
  }

  const [isEditing, setIsEditing] = useState(() =>
    shouldOpenPurchaseTermsEditView(purchaseTermsViewState)
  )
  const [datePickerSessionKey, setDatePickerSessionKey] = useState(0)
  const [pendingEditWithClearedDates, setPendingEditWithClearedDates] =
    useState(false)
  const cloneDatesClearedForOfferIdRef = useRef<string | null>(null)

  const handleEnterEditMode = useCallback(async () => {
    await setFieldValue('purchaseTermsCustomized', true)
    await setFieldValue('offerStartDate', '')
    await setFieldValue('offerExpirationDate', '')
    setFieldTouched('offerStartDate', false)
    setFieldTouched('offerExpirationDate', false)
    setPendingEditWithClearedDates(true)
  }, [setFieldValue, setFieldTouched])

  useEffect(() => {
    if (!pendingEditWithClearedDates) {
      return
    }

    if (
      !isEmpty(values.offerStartDate) ||
      !isEmpty(values.offerExpirationDate)
    ) {
      return
    }

    setDatePickerSessionKey(previousKey => previousKey + 1)
    setIsEditing(true)
    setPendingEditWithClearedDates(false)
  }, [
    pendingEditWithClearedDates,
    values.offerExpirationDate,
    values.offerStartDate,
  ])

  useEffect(() => {
    if (!acceptedDateSelected) {
      setIsEditing(true)
    }
  }, [acceptedDateSelected])

  useEffect(() => {
    if (!values.privateOfferId) {
      return
    }

    setIsEditing(shouldOpenPurchaseTermsEditView(purchaseTermsViewState))
  }, [values.privateOfferId])

  useEffect(() => {
    if (
      values.variableStartDate === undefined ||
      values.variableStartDate === null
    ) {
      setFieldValue('variableStartDate', true)
    }

    const isCustomizedClone =
      isCloneFlow && Boolean(values.purchaseTermsCustomized)

    if (
      acceptedDateSelected &&
      isEmpty(values.offerExpirationDate) &&
      !isCustomizedClone &&
      !isEditing &&
      !values.purchaseTermsCustomized
    ) {
      setFieldValue('offerExpirationDate', getDefaultPurchaseExpiryDate())
    }
  }, [
    acceptedDateSelected,
    isCloneFlow,
    isEditing,
    setFieldValue,
    values.offerExpirationDate,
    values.purchaseTermsCustomized,
    values.variableStartDate,
  ])

  useEffect(() => {
    if (
      !isCloneFlow ||
      !values.privateOfferId ||
      !values.purchaseTermsCustomized
    ) {
      return
    }

    const offerId = String(values.privateOfferId)
    if (cloneDatesClearedForOfferIdRef.current === offerId) {
      return
    }
    cloneDatesClearedForOfferIdRef.current = offerId

    const clearInitialCloneDates = async () => {
      if (!isEmpty(values.offerStartDate)) {
        await setFieldValue('offerStartDate', '')
      }
      if (!isEmpty(values.offerExpirationDate)) {
        await setFieldValue('offerExpirationDate', '')
      }
      setDatePickerSessionKey(previousKey => previousKey + 1)
    }

    void clearInitialCloneDates()
  }, [
    isCloneFlow,
    setFieldValue,
    values.privateOfferId,
    values.purchaseTermsCustomized,
  ])

  const agreementStartDateField = {
    title: 'Purchase start month',
    humanReadableKey: 'Purchase start month',
    placeHolder: 'Purchase start month',
    id: 'offerStartDate',
    isRequired: false,
    isReadOnly: isPurchaseTermsReadOnly || false,
    isCustom: false,
    dataType: 'date',
    minDate: DateTime.now().plus({ months: 1 }).startOf('month').toISO(),
  }

  const agreementEndDateField = {
    title: 'Purchase end month',
    humanReadableKey: 'Purchase end month',
    placeHolder: 'Purchase end month',
    id: 'offerExpirationDate',
    isRequired: false,
    isReadOnly: isOfferExpirationDateReadOnly || false,
    isCustom: false,
    dataType: 'date',
    minDate: 'today',
  }

  useEffect(() => {
    if (
      acceptedDateSelected ||
      isEmpty(values.offerStartDate) ||
      !values.privateOfferId
    ) {
      return
    }

    const normalized = toPurchaseStartMonthPickerDate(
      String(values.offerStartDate),
      timeZone
    )

    if (normalized && normalized !== values.offerStartDate) {
      void setFieldValue('offerStartDate', normalized)
    }
  }, [
    acceptedDateSelected,
    setFieldValue,
    timeZone,
    values.offerStartDate,
    values.privateOfferId,
  ])

  const purchaseStartMonthPickerDate = toPurchaseStartMonthPickerDate(
    values.offerStartDate as string | null | undefined,
    timeZone
  )

  const handleStartDateChange = useCallback(
    async (date: string | null) => {
      const updatedDate = date
        ? DateTime.fromISO(date, { zone: timeZone })
            .startOf('month')
            .toFormat('yyyy-MM-dd')
        : ''
      await setFieldValue('offerStartDate', updatedDate)
      await setFieldValue('purchaseTermsCustomized', true)
      await setFieldTouched('offerStartDate', true)
    },
    [setFieldValue, setFieldTouched, timeZone]
  )

  const handleEndDateChange = useCallback(
    async (date: string | null) => {
      const updatedDate = date
        ? DateTime.fromISO(date).endOf('month').toISO()
        : ''
      await setFieldValue('offerExpirationDate', updatedDate)
      await setFieldValue('purchaseTermsCustomized', true)
      await setFieldTouched('offerExpirationDate', true)
    },
    [setFieldValue, setFieldTouched]
  )

  const handleResetToDefault = useCallback(async () => {
    await setFieldValue('variableStartDate', true)
    await setFieldValue('offerStartDate', null)
    await setFieldValue('offerExpirationDate', getDefaultPurchaseExpiryDate())
    await setFieldValue('purchaseTermsCustomized', false)
    await setFieldValue(
      'metaData',
      applyPurchaseTermsMetaData(
        values.metaData as PurchaseTermsMetaDataItem[],
        false
      )
    )
    setFieldTouched('variableStartDate', false)
    setFieldTouched('offerStartDate', false)
    setFieldTouched('offerExpirationDate', false)
    setDatePickerSessionKey(previousKey => previousKey + 1)
    setIsEditing(false)
  }, [setFieldValue, setFieldTouched, values.metaData])

  const handleChangeStartDateOption = useCallback(
    async (e: SelectChangeEvent) => {
      const isAcceptedDate = parseAcceptedDateMode(e.target.value)
      await setFieldValue('variableStartDate', isAcceptedDate)
      await setFieldValue('purchaseTermsCustomized', true)

      if (isAcceptedDate) {
        await setFieldValue('offerStartDate', '')
        await setFieldValue('offerExpirationDate', '')
        setFieldTouched('offerStartDate', false)
        setFieldTouched('offerExpirationDate', false)
        setDatePickerSessionKey(previousKey => previousKey + 1)
      } else {
        await setFieldValue('offerStartDate', '')
        await setFieldValue('offerExpirationDate', '')
        setFieldTouched('offerStartDate', false)
        setFieldTouched('offerExpirationDate', false)
        setDatePickerSessionKey(previousKey => previousKey + 1)
      }
    },
    [setFieldValue, setFieldTouched]
  )

  const defaultExpiryDisplay = DateTime.now()
    .plus({ months: 1 })
    .endOf('month')
    .toFormat('MMM d, yyyy')

  const formattedExpiryDate = values.offerExpirationDate
    ? DateTime.fromISO(String(values.offerExpirationDate), {
        zone: 'utc',
      }).toFormat('MMM d, yyyy')
    : defaultExpiryDisplay

  const showCollapsedView = !isEditing

  return (
    <div data-testid="Contract-duration-section">
      <BoxV4
        heading={'Purchase terms'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '16px', height: '16px' }}
          />
        }
        secondaryActions={
          <MicrosoftFormFAQs item={MicrosoftFormFAQsItem.PURCHASE_TERMS} />
        }
      >
        <div className={classes.sectionContainer}>
          {showCollapsedView && (
            <>
              <div className={classes.noteText}>
                Once the buyer accepts the offer, they have until the end of the
                following month to complete the purchase. After that, the offer
                expires.
              </div>
              <div
                className={cx(
                  classes.summaryCard,
                  shouldBlinkCloneDates && classes.cloneDateBlink
                )}
                data-testid="purchase-terms-summary"
              >
                <div className={classes.iconWrap}>
                  <FontAwesomeIcon
                    icon={faCalendarDay}
                    style={{ width: 20, height: 20 }}
                  />
                </div>
                <div className={classes.summaryRow}>
                  Expires on {formattedExpiryDate}
                  <LabraToolTipV2
                    description="Customize purchase window."
                    tooltipPlacement="top"
                  >
                    <button
                      type="button"
                      className={classes.editButton}
                      aria-label="edit-purchase-terms"
                      onClick={handleEnterEditMode}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                  </LabraToolTipV2>
                </div>
              </div>
            </>
          )}

          {isEditing && (
            <>
              <NoteV2
                backgroundColor={NoteColors.BLUE}
                description={
                  'Set the window during which the buyer can purchase under private pricing. If no purchase is made by the last date of the end month, the offer will expire even if already accepted.'
                }
              />
              <Grid container width={'100%'} spacing={3}>
                <Grid size={6}>
                  <SelectV4
                    field={{
                      title: 'Purchase start date',
                      placeHolder: 'Purchase start date',
                      humanReadableKey: 'Purchase start date',
                      id: 'variableStartDate',
                      isRequired: false,
                      isReadOnly: false,
                      isCustom: false,
                      defaultValue: ACCEPTED_DATE_LABEL,
                      dataType: 'enum' as DataType,
                      options: startDateOptions,
                    }}
                    size="small"
                    value={acceptedDateSelected}
                    onChange={handleChangeStartDateOption}
                    onBlur={handleBlur}
                    isError={
                      !!(
                        get(touched, 'variableStartDate', false) &&
                        get(errors, 'variableStartDate', false)
                      )
                    }
                    errorText={
                      (!!get(touched, 'variableStartDate', false) &&
                        get(errors, 'variableStartDate', '')) ||
                      ''
                    }
                  />
                </Grid>

                {!isOfferStartDateHidden && (
                  <>
                    {!acceptedDateSelected && (
                      <Grid
                        size={6}
                        className={cx(
                          classes.datePickerErrorOutline,
                          shouldBlinkCloneDates && classes.cloneDateBlink
                        )}
                        data-testid="clone-date-blink-start"
                      >
                        <DatePickerV2
                          format={monthYearWithComaFormat}
                          field={{
                            ...agreementStartDateField,
                            defaultValue:
                              purchaseStartMonthPickerDate ?? undefined,
                            isReadOnly: isOfferStartDateReadOnly || false,
                          }}
                          formik={formikProps}
                          key={`offerStartDate-${datePickerSessionKey}`}
                          showErrorHelper={
                            !!touched.offerStartDate &&
                            !isEmpty(errors.offerStartDate)
                          }
                          fieldId="offerStartDate"
                          handleDateChange={handleStartDateChange}
                          errorHelperText={
                            (!!touched.offerStartDate &&
                              errors.offerStartDate) ||
                            ''
                          }
                          views={['year', 'month']}
                          size="small"
                          validate={true}
                        />
                      </Grid>
                    )}
                  </>
                )}
                {!isOfferExpirationDateHidden && (
                  <>
                    {acceptedDateSelected && (
                      <Grid
                        size={6}
                        className={cx(
                          classes.datePickerErrorOutline,
                          shouldBlinkCloneDates && classes.cloneDateBlink
                        )}
                        data-testid="clone-date-blink-end"
                      >
                        <DatePickerV2
                          format={monthYearWithComaFormat}
                          field={{
                            ...agreementEndDateField,
                            defaultValue: isEmpty(values?.offerExpirationDate)
                              ? undefined
                              : values.offerExpirationDate,
                            isReadOnly: isOfferExpirationDateReadOnly || false,
                          }}
                          formik={formikProps}
                          key={`offerExpirationDate-accepted-${datePickerSessionKey}`}
                          showErrorHelper={
                            !!touched.offerExpirationDate &&
                            !isEmpty(errors.offerExpirationDate)
                          }
                          fieldId="offerExpirationDate"
                          handleDateChange={handleEndDateChange}
                          errorHelperText={
                            (!!touched.offerExpirationDate &&
                              errors.offerExpirationDate) ||
                            ''
                          }
                          views={['year', 'month']}
                          size="small"
                          validate={true}
                        />
                      </Grid>
                    )}

                    {!acceptedDateSelected && (
                      <Grid
                        size={6}
                        className={cx(
                          classes.datePickerErrorOutline,
                          shouldBlinkCloneDates && classes.cloneDateBlink
                        )}
                        data-testid="clone-date-blink-end-specified"
                      >
                        <DatePickerV2
                          format={monthYearWithComaFormat}
                          field={{
                            ...agreementEndDateField,
                            defaultValue: isEmpty(values?.offerExpirationDate)
                              ? undefined
                              : values.offerExpirationDate,
                            isReadOnly: isOfferExpirationDateReadOnly || false,
                          }}
                          formik={formikProps}
                          key={`offerExpirationDate-specified-${datePickerSessionKey}`}
                          showErrorHelper={
                            !!touched.offerExpirationDate &&
                            !isEmpty(errors.offerExpirationDate)
                          }
                          fieldId="offerExpirationDate"
                          handleDateChange={handleEndDateChange}
                          errorHelperText={
                            (!!touched.offerExpirationDate &&
                              errors.offerExpirationDate) ||
                            ''
                          }
                          views={['year', 'month']}
                          size="small"
                          validate={true}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>

              <div className={classes.resetRow}>
                <ButtonV2 styleType="link" onClick={handleResetToDefault}>
                  Reset to default
                </ButtonV2>
              </div>
            </>
          )}
        </div>
      </BoxV4>
    </div>
  )
}
